import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { AuthService } from 'src/app/services/auth.service';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { TaxonomyOverridesService } from 'src/app/services/taxonomy-overrides.service';
import { LoggerService } from '../../../services/logger.service';

/**
 * Per-studio PayBright configuration screen.
 *
 * Aimed at studio operators, not developers: they paste the source key (and
 * optional PIN) and the terminal's serial number, then check the connection.
 * There is no OAuth/authorize step like Square and no environment switch. The
 * gateway environment is resolved server-side. Settings are stored on the
 * studio's paybright_api configuration entity via the generic loadStudioConfig
 * / saveStudioConfig endpoints.
 *
 * The environment and currency values are not shown in the UI but are carried
 * through load → save unchanged so a value set directly in config is preserved.
 */
@Component({
  selector: 'app-form-paybright-settings',
  templateUrl: './form-paybright-settings.component.html',
  styleUrls: ['./form-paybright-settings.component.css'],
})
export class FormPaybrightSettingsComponent implements OnInit {
  paybrightSettingsForm: FormGroup;
  showProgressSpinner = false;
  successMessage: string;
  errorMessage: string;
  verifyMessage: string;
  verifyOk: boolean | null = null;
  /** Starts true: a saved-credentials studio should read "Checking your
   *  connection…" on first paint, never "Not yet verified". */
  verifying = true;

  webhookMessage: string;
  webhookOk: boolean | null = null;
  registeringWebhook = false;

  private readonly configFieldName = 'field_paybright_ref';

  constructor(
    private fb: FormBuilder,
    private drupalRESTService: DrupalRESTService,
    private _authService: AuthService,
    private taxonomyOverridesService: TaxonomyOverridesService,
  ) { }

  ngOnInit(): void {
    this.paybrightSettingsForm = this.fb.group({
      paybrightEnabled: [false],
      paybrightSourceKey: [''],
      paybrightPin: [''],
      paybrightTerminalId: [''],
      // Carried through unchanged; not shown to operators.
      paybrightEnvironment: [''],
      transactionCurrency: ['USD'],
    });

    this.loadPaybrightSettings();
  }

  loadPaybrightSettings() {
    this.drupalRESTService
      .httpGET(`/api_rest/v1/loadStudioConfig?config_field_name=${this.configFieldName}`)
      .subscribe(
        (response) => {
          // '1'/'0' string booleans come back from Drupal, so coerce to boolean.
          const enabled = response?.['field_paybright_enabled']?.[0]?.value;
          this.paybrightSettingsForm.patchValue({
            paybrightEnabled: enabled === true || enabled === '1' || enabled === 1,
            paybrightSourceKey: response?.['field_paybright_source_key']?.[0]?.value ?? '',
            paybrightPin: response?.['field_paybright_pin']?.[0]?.value ?? '',
            paybrightTerminalId: response?.['field_paybright_terminal_id']?.[0]?.value ?? '',
            paybrightEnvironment: response?.['field_paybright_environment']?.[0]?.value ?? '',
            transactionCurrency: response?.['field_transaction_currency']?.[0]?.value ?? 'USD',
          });

          // The verified state is not persisted. Re-check silently on load so
          // a studio with working credentials doesn't reload into "Not yet
          // verified". Skip when no source key is saved yet.
          if (this.paybrightSettingsForm.value.paybrightSourceKey && this._authService.selectedStudio?.id) {
            this.verifyCredentials();
          } else {
            // Nothing to verify: settle on the genuine "not yet verified" state.
            this.verifying = false;
          }
        },
        (error) => {
          // A studio with no PayBright config yet simply starts blank.
          LoggerService.error('Error loading PayBright settings:', error);
          this.verifying = false;
        }
      );
  }

  savePaybrightSettings() {
    const v = this.paybrightSettingsForm.value;
    const postData = {
      config_field_name: this.configFieldName,
      config_data: {
        field_paybright_enabled: [{ value: v.paybrightEnabled ? 1 : 0 }],
        field_paybright_source_key: [{ value: v.paybrightSourceKey }],
        field_paybright_pin: [{ value: v.paybrightPin }],
        field_paybright_terminal_id: [{ value: v.paybrightTerminalId }],
        field_paybright_environment: [{ value: v.paybrightEnvironment }],
        field_transaction_currency: [{ value: v.transactionCurrency }],
      },
    };

    this.showProgressSpinner = true;
    this.successMessage = '';
    this.errorMessage = '';

    this.drupalRESTService.httpPOST('/api_rest/v1/saveStudioConfig', postData).subscribe(
      () => {
        this.showProgressSpinner = false;
        // Saving an enabled config with a source key turns the PayBright
        // payment methods on server-side; reload the studio's overrides so the
        // payment form's picker shows them without a page reload.
        const enabledWithKey = !!v.paybrightEnabled && !!(v.paybrightSourceKey || '').trim();
        this.successMessage = enabledWithKey
          ? 'PayBright settings saved. The PayBright payment methods are now available on the payment form (manage them under Payment Methods).'
          : 'PayBright settings saved.';
        this.taxonomyOverridesService.refresh();
      },
      (error) => {
        this.showProgressSpinner = false;
        LoggerService.error('Error saving PayBright settings:', error);
        this.errorMessage = 'Error saving PayBright settings.';
      }
    );
  }

  verifyCredentials() {
    const studioId = this._authService.selectedStudio?.id;
    if (!studioId) {
      this.verifyOk = false;
      this.verifyMessage = 'No studio selected.';
      return;
    }

    this.verifyMessage = 'Checking…';
    this.verifyOk = null;
    this.verifying = true;

    this.drupalRESTService.httpGET(`/api/paybright/verify/${studioId}`).subscribe(
      (response: any) => {
        this.verifying = false;
        this.verifyOk = !!response?.valid;
        this.verifyMessage = response?.valid
          ? ''
          : (response?.error || 'We couldn’t connect with these details. Double-check your source key and PIN.');
      },
      (error) => {
        this.verifying = false;
        this.verifyOk = false;
        this.verifyMessage = error?.error?.error || 'We couldn’t reach PayBright. Please try again in a moment.';
      }
    );
  }

  /**
   * Registers this studio's webhook with PayBright so settlement/decline events
   * sync back automatically. The backend builds the callback URL and stores the
   * returned signature key. Requires an HTTPS-reachable host.
   */
  registerWebhook() {
    const studioId = this._authService.selectedStudio?.id;
    if (!studioId) {
      this.webhookMessage = 'No studio selected.';
      this.webhookOk = false;
      return;
    }

    this.registeringWebhook = true;
    this.webhookMessage = '';
    this.webhookOk = null;

    this.drupalRESTService.httpPOST(`/api/paybright/webhook/register/${studioId}`, {}).subscribe(
      (response: any) => {
        this.registeringWebhook = false;
        this.webhookOk = !!response?.success;
        this.webhookMessage = response?.success
          ? `Webhook registered at ${response.webhook_url}.`
          : (response?.error || 'Could not register the webhook.');
      },
      (error) => {
        this.registeringWebhook = false;
        this.webhookOk = false;
        this.webhookMessage = error?.error?.error || 'Could not register the webhook. The site must be reachable over HTTPS.';
      }
    );
  }
}
